import qs from "qs";
import axios from "./AxiosClient";
import { Constants } from "@socialcoach/services";
import { scCatchServiceException } from "@/annotations";
import {
  BackgroundAsset,
  BackgroundAssetPatch,
  BackgroundAssetVersion,
  BackgroundFacetValues,
  BackgroundRatio,
  BackgroundReframeMode,
  BackgroundRequest,
  BackgroundSearchBody,
  BackgroundSearchPage,
  BackgroundSearchParams,
  BackgroundsQueueStatus,
  BackgroundVisibility,
  GenerateBackgroundsRequest,
  Page,
  RefineBackgroundRequest,
  UploadBackgroundRequest,
} from "./dtos";

const BACKGROUND_ASSET_SERVICE = `${Constants.API_URL}/bedrock/background_assets`;
const BACKGROUNDS_QUEUE_SERVICE = `${Constants.API_URL}/bedrock/backgrounds/queue`;

/** Repeated filters go out as `setting[]=a&setting[]=b`, the form Rails reads as arrays. */
export const bracketParams = (params: object) => qs.stringify(params, { arrayFormat: "brackets" });

/**
 * The background library. Every method rethrows the parsed `{ error, message, details }` envelope so
 * callers can discriminate with `isApiError(e, code)`.
 */
export class BackgroundAssetServices {
  @scCatchServiceException(true)
  static async list(params: BackgroundSearchParams): Promise<Page<BackgroundAsset>> {
    const { data } = await axios.get<Page<BackgroundAsset>>(BACKGROUND_ASSET_SERVICE, {
      params,
      paramsSerializer: bracketParams,
    });
    return data;
  }

  @scCatchServiceException(true)
  static async search(body: BackgroundSearchBody): Promise<BackgroundSearchPage> {
    const { data } = await axios.post<BackgroundSearchPage>(`${BACKGROUND_ASSET_SERVICE}/search`, body);
    return data;
  }

  @scCatchServiceException(true)
  static async get(id: number): Promise<BackgroundAsset> {
    const { data } = await axios.get<BackgroundAsset>(`${BACKGROUND_ASSET_SERVICE}/${id}`);
    return data;
  }

  @scCatchServiceException(true)
  static async upload(request: UploadBackgroundRequest): Promise<BackgroundAsset> {
    const { data } = await axios.post<BackgroundAsset>(BACKGROUND_ASSET_SERVICE, request);
    return data;
  }

  @scCatchServiceException(true)
  static async generate(request: GenerateBackgroundsRequest): Promise<{ requests: BackgroundRequest[] }> {
    const { data } = await axios.post<{ requests: BackgroundRequest[] }>(
      `${BACKGROUND_ASSET_SERVICE}/generate`,
      request
    );
    return data;
  }

  @scCatchServiceException(true)
  static async refine(id: number, request: RefineBackgroundRequest): Promise<BackgroundRequest> {
    const { data } = await axios.post<BackgroundRequest>(`${BACKGROUND_ASSET_SERVICE}/${id}/refine`, request);
    return data;
  }

  @scCatchServiceException(true)
  static async reframe(id: number, ratio: BackgroundRatio, mode?: BackgroundReframeMode): Promise<BackgroundRequest> {
    const { data } = await axios.post<BackgroundRequest>(
      `${BACKGROUND_ASSET_SERVICE}/${id}/reframe`,
      mode ? { ratio, mode } : { ratio }
    );
    return data;
  }

  @scCatchServiceException(true)
  static async update(id: number, patch: BackgroundAssetPatch): Promise<BackgroundAsset> {
    const { data } = await axios.patch<BackgroundAsset>(`${BACKGROUND_ASSET_SERVICE}/${id}`, patch);
    return data;
  }

  /** Admin only: moves a coach asset to the global library. */
  @scCatchServiceException(true)
  static async promote(
    id: number,
    visibility?: Extract<BackgroundVisibility, "active" | "draft">
  ): Promise<BackgroundAsset> {
    const { data } = await axios.post<BackgroundAsset>(
      `${BACKGROUND_ASSET_SERVICE}/${id}/promote`,
      visibility ? { visibility } : {}
    );
    return data;
  }

  /** Admin only. */
  @scCatchServiceException(true)
  static async updateVersion(
    id: number,
    versionId: number,
    status: Extract<BackgroundAssetVersion["status"], "rejected" | "ready">
  ): Promise<BackgroundAsset> {
    const { data } = await axios.patch<BackgroundAsset>(`${BACKGROUND_ASSET_SERVICE}/${id}/versions/${versionId}`, {
      status,
    });
    return data;
  }

  @scCatchServiceException(true)
  static async facets(): Promise<BackgroundFacetValues> {
    const { data } = await axios.get<BackgroundFacetValues>(`${BACKGROUND_ASSET_SERVICE}/facets`);
    return data;
  }

  /** Admin only. */
  @scCatchServiceException(true)
  static async queue(): Promise<BackgroundsQueueStatus> {
    const { data } = await axios.get<BackgroundsQueueStatus>(BACKGROUNDS_QUEUE_SERVICE);
    return data;
  }
}
